// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * Local call recorder — the artefact. Composites every visible tile onto a
 * canvas and mixes every audio stream into one track, then encodes to WebM with
 * MediaRecorder entirely on-device. The recording is a local file; it is never
 * uploaded. Peers are told over the data channel that recording is on.
 */
import { computeGridColumns } from './mesh.ts';

export interface RecordSource {
  video: HTMLVideoElement | null;
  label: string;
  camOn: boolean;
}

const MIME_CANDIDATES = [
  'video/webm;codecs=vp9,opus',
  'video/webm;codecs=vp8,opus',
  'video/webm;codecs=h264,opus',
  'video/webm',
  'video/mp4',
];

export function pickRecordMime(): string | null {
  if (typeof MediaRecorder === 'undefined') return null;
  for (const m of MIME_CANDIDATES) {
    try {
      if (MediaRecorder.isTypeSupported(m)) return m;
    } catch {
      /* keep trying */
    }
  }
  return '';
}

export function recorderSupported(): boolean {
  return typeof MediaRecorder !== 'undefined' && pickRecordMime() !== null;
}

const W = 1280;
const H = 720;

export class CallRecorder {
  private canvas = document.createElement('canvas');
  private ctx: CanvasRenderingContext2D;
  private timer: ReturnType<typeof setInterval> | null = null;
  private rec: MediaRecorder | null = null;
  private chunks: Blob[] = [];
  private audioDest: MediaStreamAudioDestinationNode | null = null;
  private connected = new Map<MediaStream, MediaStreamAudioSourceNode>();
  private startedAt = 0;
  mime = '';

  constructor(
    private audioCtx: AudioContext,
    private getSources: () => RecordSource[],
  ) {
    this.canvas.width = W;
    this.canvas.height = H;
    const c = this.canvas.getContext('2d');
    if (!c) throw new Error('canvas 2d unavailable');
    this.ctx = c;
  }

  /** Begin recording. `startedAt` is passed in to keep this module clock-free. */
  start(startedAt: number, initialAudio: MediaStream[], fps = 20): void {
    this.startedAt = startedAt;
    this.audioDest = this.audioCtx.createMediaStreamDestination();
    this.syncAudio(initialAudio);

    const canvasStream = this.canvas.captureStream(fps);
    const mixed = new MediaStream();
    for (const t of canvasStream.getVideoTracks()) mixed.addTrack(t);
    for (const t of this.audioDest.stream.getAudioTracks()) mixed.addTrack(t);

    const mime = pickRecordMime() ?? '';
    this.mime = mime || 'video/webm';
    this.rec = mime ? new MediaRecorder(mixed, { mimeType: mime }) : new MediaRecorder(mixed);
    this.chunks = [];
    this.rec.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) this.chunks.push(e.data);
    };
    this.rec.start(1000);
    // setInterval (not rAF) so the compositor keeps ticking in a backgrounded tab.
    this.timer = setInterval(() => this.draw(), Math.round(1000 / fps));
    this.draw();
  }

  /** Reconcile which audio streams feed the mixdown (people join/leave mid-record). */
  syncAudio(streams: MediaStream[]): void {
    if (!this.audioDest) return;
    const wanted = new Set(streams.filter((s) => s.getAudioTracks().length > 0));
    for (const [stream, node] of this.connected) {
      if (!wanted.has(stream)) {
        try {
          node.disconnect();
        } catch {
          /* gone */
        }
        this.connected.delete(stream);
      }
    }
    for (const stream of wanted) {
      if (this.connected.has(stream)) continue;
      try {
        const node = this.audioCtx.createMediaStreamSource(stream);
        node.connect(this.audioDest);
        this.connected.set(stream, node);
      } catch {
        /* stream not connectable */
      }
    }
  }

  private draw(): void {
    const ctx = this.ctx;
    ctx.fillStyle = '#0a0f14';
    ctx.fillRect(0, 0, W, H);

    const sources = this.getSources();
    const n = Math.max(1, sources.length);
    const cols = computeGridColumns(n);
    const rows = Math.ceil(n / cols);
    const gap = 8;
    const cw = (W - gap * (cols + 1)) / cols;
    const ch = (H - gap * (rows + 1)) / rows;

    for (let i = 0; i < sources.length; i++) {
      const s = sources[i];
      const col = i % cols;
      const row = Math.floor(i / cols);
      const x = gap + col * (cw + gap);
      const y = gap + row * (ch + gap);

      ctx.fillStyle = '#121a22';
      roundRect(ctx, x, y, cw, ch, 10);
      ctx.fill();

      if (s.camOn && s.video && s.video.videoWidth > 0) {
        ctx.save();
        roundRect(ctx, x, y, cw, ch, 10);
        ctx.clip();
        drawCover(ctx, s.video, x, y, cw, ch);
        ctx.restore();
      } else {
        ctx.fillStyle = '#5a6b7a';
        ctx.font = '600 34px system-ui, sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(initials(s.label), x + cw / 2, y + ch / 2);
      }

      // name pill
      ctx.fillStyle = 'rgba(0,0,0,0.55)';
      ctx.font = '500 20px system-ui, sans-serif';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'alphabetic';
      const label = s.label || 'Guest';
      const tw = ctx.measureText(label).width;
      roundRect(ctx, x + 12, y + ch - 40, tw + 20, 28, 6);
      ctx.fill();
      ctx.fillStyle = '#e8eef4';
      ctx.fillText(label, x + 22, y + ch - 20);
    }

    // REC watermark + elapsed
    const secs = Math.floor((this.startedAt ? nowMs() - this.startedAt : 0) / 1000);
    ctx.fillStyle = '#ff5b6a';
    ctx.beginPath();
    ctx.arc(28, 28, 8, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#e8eef4';
    ctx.font = '600 20px system-ui, sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText(`REC  ${fmtDur(secs)}`, 44, 34);
  }

  async stop(): Promise<Blob> {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
    const rec = this.rec;
    const blob = await new Promise<Blob>((resolve) => {
      if (!rec) {
        resolve(new Blob(this.chunks, { type: this.mime }));
        return;
      }
      rec.onstop = () => resolve(new Blob(this.chunks, { type: this.mime }));
      try {
        rec.stop();
      } catch {
        resolve(new Blob(this.chunks, { type: this.mime }));
      }
    });
    for (const [, node] of this.connected) {
      try {
        node.disconnect();
      } catch {
        /* gone */
      }
    }
    this.connected.clear();
    this.audioDest = null;
    this.rec = null;
    return blob;
  }
}

function nowMs(): number {
  return performance.now();
}

export function fmtDur(secs: number): string {
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function initials(name: string): string {
  const parts = (name || 'Guest').trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() ?? '').join('') || 'G';
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

function drawCover(ctx: CanvasRenderingContext2D, v: HTMLVideoElement, x: number, y: number, w: number, h: number): void {
  const vr = v.videoWidth / v.videoHeight;
  const dr = w / h;
  let sw = v.videoWidth;
  let sh = v.videoHeight;
  let sx = 0;
  let sy = 0;
  if (vr > dr) {
    sw = v.videoHeight * dr;
    sx = (v.videoWidth - sw) / 2;
  } else {
    sh = v.videoWidth / dr;
    sy = (v.videoHeight - sh) / 2;
  }
  ctx.drawImage(v, sx, sy, sw, sh, x, y, w, h);
}
